import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AppShell } from "@/components/AppShell";
import { Clock, Trash2 } from "lucide-react";
import { readRecent, clearRecent } from "@/lib/recently-viewed";
import { getRecentlyViewedOrders } from "@/lib/buy-orders.functions";
import { PriceInline } from "@/lib/currency";

export const Route = createFileRoute("/_authenticated/recently-viewed")({
  head: () => ({ meta: [{ title: "Recently Viewed — Want To Buy" }] }),
  component: RecentlyViewedPage,
});

const TCG_LABEL: Record<string, string> = {
  pokemon: "Pokémon",
  yugioh: "Yu-Gi-Oh!",
  mtg: "Magic: The Gathering",
  lorcana: "Lorcana",
  one_piece: "One Piece",
  digimon: "Digimon",
  other: "Other",
};

function formatPrice(cents: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

function RecentlyViewedPage() {
  const fetchRecent = useServerFn(getRecentlyViewedOrders);
  const [ids, setIds] = useState<string[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setIds(readRecent());
    setReady(true);
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ["recently-viewed", ids],
    queryFn: () => fetchRecent({ data: { ids } }),
    enabled: ready && ids.length > 0,
  });

  function clearAll() {
    clearRecent();
    setIds([]);
  }

  const orders = data?.orders ?? [];

  return (
    <AppShell>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <Clock className="h-7 w-7 text-primary" /> Recently Viewed
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Last 50 buy orders you opened.</p>
        </div>
        {ids.length > 0 && (
          <button onClick={clearAll} className="btn-ghost inline-flex items-center gap-1.5 px-3 py-1.5 text-xs">
            <Trash2 className="h-3.5 w-3.5" /> Clear all
          </button>
        )}
      </div>

      {ids.length === 0 ? (
        <div className="panel p-10 text-center text-sm text-muted-foreground">
          You haven't viewed any orders yet.{" "}
          <Link to="/" className="text-primary hover:underline">Browse buy orders</Link> to get started.
        </div>
      ) : isLoading ? (
        <div className="panel p-10 text-center text-sm text-muted-foreground">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="panel p-10 text-center text-sm text-muted-foreground">
          None of your recently viewed orders are available anymore.
        </div>
      ) : (
        <div className="panel divide-y divide-border">
          {orders.map((o) => (
            <Link
              key={o.id}
              to="/orders/$id"
              params={{ id: o.id }}
              className="block p-4 text-sm hover:bg-muted/30"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{o.card_name || o.title}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {TCG_LABEL[o.tcg] ?? o.tcg} · {o.status}
                  </div>
                </div>
                <div className="text-sm font-mono">
                  up to <PriceInline usd={o.max_price_cents / 100} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </AppShell>
  );
}
